import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import React, { Suspense, lazy } from "react";

// Critical pages loaded eagerly
import LoginPage from "./pages/LoginPage";

// All other pages loaded lazily (code splitting)
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const RestaurantsPage = lazy(() => import("./pages/RestaurantsPage"));
const RestaurantDetailPage = lazy(() => import("./pages/RestaurantDetailPage"));
const MenuPage = lazy(() => import("./pages/MenuPage"));
const StaffPage = lazy(() => import("./pages/StaffPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const CompetitorsPage = lazy(() => import("./pages/CompetitorsPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const AIInsightsPage = lazy(() => import("./pages/AIInsightsPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const MagazzinoPlaceholderPage = lazy(() => import("./pages/MagazzinoPlaceholderPage"));
const ViabilityTestPage = lazy(() => import("./pages/ViabilityTestPage"));
const DailyTrackerPage = lazy(() => import("./pages/DailyTrackerPage"));
const BenchmarkPage = lazy(() => import("./pages/BenchmarkPage"));
const PlatformBenchmarkPage = lazy(() => import("./pages/PlatformBenchmarkPage"));
const OperationsPage = lazy(() => import("./pages/OperationsPage"));
const MonthComparisonPage = lazy(() => import("./pages/MonthComparisonPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const ExpenseAnalysisPage = lazy(() => import("./pages/ExpenseAnalysisPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const SuperAdminLeadsPage = lazy(() => import("./pages/SuperAdminLeadsPage"));
const SuperAdminProspectsPage = lazy(() => import("./pages/SuperAdminProspectsPage"));
const CashFlowPage = lazy(() => import("./pages/CashFlowPage"));
const MenuEngineeringPage = lazy(() => import("./pages/MenuEngineeringPage"));
const RevenueAIPage = lazy(() => import("./pages/RevenueAIPage"));
const StaffAnalysisPage = lazy(() => import("./pages/StaffAnalysisPage"));
const CashFlowSimulatorPage = lazy(() => import("./pages/CashFlowSimulatorPage"));
const FoodCostPage = lazy(() => import("./pages/FoodCostPage"));
const PlansManagementPage = lazy(() => import("./pages/PlansManagementPage"));
const DemoCalendarSettingsPage = lazy(() => import("./pages/DemoCalendarSettingsPage"));
const FranchiseManagementPage = lazy(() => import("./pages/FranchiseManagementPage"));
const MasterDashboardPage = lazy(() => import("./pages/MasterDashboardPage"));
const PosConfigPage = lazy(() => import("./pages/PosConfigPage"));
const ProfitLossPage = lazy(() => import("./pages/ProfitLossPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const FoodCostSeoPage = lazy(() => import("./pages/seo/FoodCostSeoPage"));
const GestionaleSeoPage = lazy(() => import("./pages/seo/GestionaleSeoPage"));
const BenchmarkSeoPage = lazy(() => import("./pages/seo/BenchmarkSeoPage"));
const PrezziSeoPage = lazy(() => import("./pages/seo/PrezziSeoPage"));
const ContattiSeoPage = lazy(() => import("./pages/seo/ContattiSeoPage"));
const BreakEvenSeoPage = lazy(() => import("./pages/seo/BreakEvenSeoPage"));
const KpiSeoPage = lazy(() => import("./pages/seo/KpiSeoPage"));
const CashFlowSeoPage = lazy(() => import("./pages/seo/CashFlowSeoPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const CalcolatoreProfittiPage = lazy(() => import("./pages/CalcolatoreProfittiPage"));
const BioLinkPage = lazy(() => import("./pages/BioLinkPage"));
const GoogleAdsLandingPage = lazy(() => import("./pages/GoogleAdsLandingPage"));
const SuperAdminBioStatsPage = lazy(() => import("./pages/SuperAdminBioStatsPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const UpgradePage = lazy(() => import("./pages/UpgradePage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));

// Components
import Layout from "./components/Layout";
import { FeatureGate } from "./components/FeatureGate";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-4 border-[#2E4A3D] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { token, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-4 border-[#2E4A3D] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (token) {
    return <Navigate to={user?.role === 'superadmin' ? '/superadmin' : user?.role === 'master' ? '/master/dashboard' : '/dashboard'} replace />;
  }
  
  return children;
};

const SuspenseFallback = () => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#fff'}}>
    <div style={{width:32,height:32,border:'3px solid #6BBE44',borderTopColor:'transparent',borderRadius:'50%',animation:'rspin 1s linear infinite'}} />
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/software-food-cost-ristorante" element={<FoodCostSeoPage />} />
      <Route path="/gestionale-ristorante" element={<GestionaleSeoPage />} />
      <Route path="/benchmark-ristorante" element={<BenchmarkSeoPage />} />
      <Route path="/prezzi" element={<PrezziSeoPage />} />
      <Route path="/contatti" element={<ContattiSeoPage />} />
      <Route path="/break-even-ristorante" element={<BreakEvenSeoPage />} />
      <Route path="/software-kpi-ristorante" element={<KpiSeoPage />} />
      <Route path="/cash-flow-ristorante" element={<CashFlowSeoPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/calcolatore-gratis" element={<CalcolatoreProfittiPage />} />
      <Route path="/profit-calculator" element={<CalcolatoreProfittiPage />} />
      <Route path="/calcolatore" element={<CalcolatoreProfittiPage />} />
      <Route path="/link" element={<BioLinkPage />} />
      <Route path="/google-ads-lp" element={<GoogleAdsLandingPage />} />
      <Route path="/lp/google" element={<GoogleAdsLandingPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/restaurants" element={<RestaurantsPage />} />
        <Route path="/restaurants/:id" element={<RestaurantDetailPage />} />
        <Route path="/restaurants/:id/menu" element={<MenuPage />} />
        <Route path="/restaurants/:id/staff" element={<StaffPage />} />
        <Route path="/restaurants/:id/expenses" element={<ExpensesPage />} />
        <Route path="/restaurants/:id/integrations" element={<IntegrationsPage />} />
        <Route path="/restaurants/:id/competitors" element={<FeatureGate feature="competitors"><CompetitorsPage /></FeatureGate>} />
        <Route path="/restaurants/:id/viability" element={<FeatureGate feature="stress_test"><ViabilityTestPage /></FeatureGate>} />
        <Route path="/restaurants/:id/daily" element={<DailyTrackerPage />} />
        <Route path="/restaurants/:id/pos-config" element={<PosConfigPage />} />
        <Route path="/restaurants/:id/benchmark" element={<FeatureGate feature="benchmark"><BenchmarkPage /></FeatureGate>} />
        <Route path="/restaurants/:id/platform-benchmark" element={<FeatureGate feature="benchmark"><PlatformBenchmarkPage /></FeatureGate>} />
        <Route path="/restaurants/:id/operations" element={<OperationsPage />} />
        <Route path="/restaurants/:id/reports" element={<FeatureGate feature="month_comparison"><MonthComparisonPage /></FeatureGate>} />
        <Route path="/restaurants/:id/expense-analysis" element={<FeatureGate feature="expense_alerts"><ExpenseAnalysisPage /></FeatureGate>} />
        <Route path="/restaurants/:id/cash-flow" element={<FeatureGate feature="cash_flow"><CashFlowPage /></FeatureGate>} />
        <Route path="/restaurants/:id/menu-engineering" element={<FeatureGate feature="menu_engineering"><MenuEngineeringPage /></FeatureGate>} />
        <Route path="/restaurants/:restaurantId/revenue-ai" element={<ProtectedRoute><RevenueAIPage /></ProtectedRoute>} />
        <Route path="/restaurants/:id/staff-analysis" element={<FeatureGate feature="staff_optimizer"><StaffAnalysisPage /></FeatureGate>} />
        <Route path="/restaurants/:id/cash-flow-simulator" element={<FeatureGate feature="scenario_simulator"><CashFlowSimulatorPage /></FeatureGate>} />
        <Route path="/restaurants/:id/food-cost" element={<FoodCostPage />} />
        <Route path="/restaurants/:id/pnl" element={<FeatureGate feature="pnl"><ProfitLossPage /></FeatureGate>} />
        <Route path="/superadmin/plans" element={<PlansManagementPage />} />
        <Route path="/superadmin/franchise" element={<FranchiseManagementPage />} />
        <Route path="/superadmin/leads" element={<SuperAdminLeadsPage />} />
        <Route path="/superadmin/prospects" element={<SuperAdminProspectsPage />} />
        <Route path="/superadmin/bio-stats" element={<SuperAdminBioStatsPage />} />
        <Route path="/superadmin/demo-calendar" element={<DemoCalendarSettingsPage />} />
        <Route path="/master/dashboard" element={<MasterDashboardPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/superadmin" element={<SuperAdminPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/comparison" element={<ComparisonPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/ai-insights" element={<FeatureGate feature="ai_insights"><AIInsightsPage /></FeatureGate>} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/magazzino" element={<MagazzinoPlaceholderPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/upgrade" element={<UpgradePage />} />
      </Route>
      
      {/* Payment routes (protected but outside Layout) */}
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
    <AuthProvider>
    <CurrencyProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </CurrencyProvider>
    </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
